/**
 * Pushimi i lehonisë — periudha totale dhe tri etapat e saj.
 *
 * NJË kërkesë pushimi, tri etapa. Ky komponent nuk krijon kurrë tri kërkesa:
 * merr `holidayId`-në e kërkesës ekzistuese dhe punon me fëmijët e saj
 * (`/api/maternity-leave/:holidayId/stages`). Tri kërkesa do të thoshin tri
 * aprovime dhe tri mundësi që e njëjta lehoni të numërohej dy herë.
 *
 * Asnjë rregull lehonie nuk jeton këtu: datat, muajt, përqindja dhe paguesi
 * vijnë të llogaritura nga `backend/services/maternityStages.js`, me normat dhe
 * gjatësitë nga cilësimet e kompanisë. Ky skedar i rendon dhe lejon HR-në t'i
 * zhvendosë datat — asgjë më shumë.
 *
 * Statusi nuk ruhet: një etapë që mbaroi dje lexohet e mbaruar sot pa i shkruar
 * askush asgjë. Prandaj `StatusChip` merr atë që kthen serveri, çdo herë.
 *
 * ETAPA E FUNDIT — 3 muaj pa pagesë — ËSHTË OPSIONALE dhe e fikur në nisje.
 * Kutia këtu vetëm e dërgon zgjedhjen (`IncludeUnpaid`); datat, muajt dhe numri
 * i etapave vijnë prapë të llogaritura nga serveri. Kur është e fikur, për atë
 * fazë nuk ekziston asnjë rresht — jo një rresht i fshehur.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Button, TextField, Alert, AlertTitle,
  Skeleton, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, Tooltip, IconButton, Switch, FormControlLabel,
} from '@mui/material';
import EditRoundedIcon        from '@mui/icons-material/EditRounded';
import ChildFriendlyRounded   from '@mui/icons-material/ChildFriendlyRounded';
import RefreshRoundedIcon     from '@mui/icons-material/RefreshRounded';
import { authFetch } from '../../services/apiServices';
import StatusChip from '../common/StatusChip';

/** Paguesi → etiketa e biznesit. Serveri i dërgon të dyja; kjo është rezerva. */
export const PAYER_LABEL = {
  COMPANY: 'Kompania',
  STATE:   'Shteti',
  UNPAID:  'Pa pagesë',
};

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '—');

/**
 * Periudha si një varg i vetëm. Kur mungon njëri skaj shfaqet viza, jo
 * "Invalid Date" — një etapë pa datë është gjendje e mundshme pas një
 * zhvendosjeje të pambaruar.
 */
export const fmtRange = (a, b) => `${fmtDate(a)} – ${fmtDate(b)}`;

export default function MaternityStages({ holidayId, startDate = null, canManage = false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [editing, setEditing] = useState(null);
  // Zgjedhja për bishtin 3-mujor pa pagesë. Nis e FIKUR; kur kërkesa i ka
  // etapat e ruajtura, pasqyron atë që është vërtet në bazë (`IncludesUnpaid`),
  // sepse aty e vërteta janë rreshtat, jo kjo gjendje.
  const [includeUnpaid, setIncludeUnpaid] = useState(false);

  const load = useCallback(async () => {
    if (!holidayId) { setLoading(false); return; }
    setLoading(true); setError('');
    try {
      const res  = await authFetch(`/api/maternity-leave/${holidayId}/stages`);
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) { setError('Nuk keni leje për të parë etapat e lehonisë.'); setData(null); return; }
      if (!res.ok || json.success === false) { setError(json.error || `Nuk u morën të dhënat (HTTP ${res.status}).`); setData(null); return; }
      setData(json.data || null);
      if (json.data?.Stages?.length) setIncludeUnpaid(!!json.data.IncludesUnpaid);
    } catch {
      setError('Nuk u lidh dot me serverin.');
      setData(null);
    } finally { setLoading(false); }
  }, [holidayId]);

  useEffect(() => { load(); }, [load]);

  /* Gjenerimi është idempotent te serveri (UNIQUE holiday_id + stage_no), ndaj
   * një klikim i dyfishtë nuk krijon etapa të dyfishta — thjesht i rishkruan.
   *
   * Flamuri kalon si argument e jo nga gjendja: kur kutia sapo është prekur,
   * `includeUnpaid` ende s'është rifreskuar dhe do të dërgohej vlera e vjetër. */
  const generate = async (withUnpaid = includeUnpaid) => {
    setBusy(true); setError('');
    try {
      const res = await authFetch(`/api/maternity-leave/${holidayId}/stages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(startDate ? { StartDate: startDate } : {}),
          IncludeUnpaid: !!withUnpaid,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        setError(json.error || 'Etapat nuk u gjeneruan.');
        // Kutia kthehet aty ku ishte: përndryshe do të tregonte një zgjedhje
        // që serveri s'e pranoi.
        setIncludeUnpaid(!!data?.IncludesUnpaid);
        return;
      }
      setData(json.data || null);
      setIncludeUnpaid(!!json.data?.IncludesUnpaid);
    } catch {
      setError('Nuk u lidh dot me serverin.');
      setIncludeUnpaid(!!data?.IncludesUnpaid);
    } finally { setBusy(false); }
  };

  /* Kutia e bishtit pa pagesë — e njëjta për krijim dhe për menaxhim.
   * `onCommit` jepet vetëm kur etapat ekzistojnë tashmë: atëherë prekja e saj
   * i rishkruan ato menjëherë. Te gjendja bosh zgjedhja rri derisa të klikohet
   * "Gjenero etapat", sepse deri atëherë s'ka çfarë të rishkruhet. */
  const unpaidToggle = (onCommit = null) => (
    <Box>
      <FormControlLabel
        sx={{ ml: 0, mr: 0 }}
        control={(
          <Switch
            size="small"
            checked={includeUnpaid}
            disabled={busy || loading}
            onChange={(e) => {
              const next = e.target.checked;
              setIncludeUnpaid(next);
              if (onCommit) onCommit(next);
            }}
          />
        )}
        label={(
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
            Përfshi edhe 3 muaj pushim pa pagesë
          </Typography>
        )}
      />
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', pl: 0.5 }}>
        {includeUnpaid
          ? 'Faza pa pagesë hyn në periudhën totale dhe e zgjat datën përfundimtare.'
          : 'E fikur: pushimi mbaron pas fazave të paguara — për fazën pa pagesë nuk krijohet asnjë etapë.'}
      </Typography>
    </Box>
  );

  const stages = useMemo(() => data?.Stages || [], [data]);

  if (!holidayId) return null;

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      {/* Koka — periudha totale rri mbi etapat, sepse ajo është ajo që lexohet e para */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: 'primary.soft', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChildFriendlyRounded sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, lineHeight: 1.25 }}>
            Pushimi i lehonisë
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
            {data?.TotalPeriod
              ? `Periudha totale: ${fmtRange(data.TotalPeriod.StartDate, data.TotalPeriod.EndDate)}`
              : 'Periudha totale nuk është caktuar ende'}
          </Typography>
        </Box>
        {data?.Status && <StatusChip status={data.Status} size="sm" />}
        <Tooltip title="Rifresko">
          <span>
            <IconButton size="small" onClick={load} disabled={loading || busy} sx={{ width: 30, height: 30 }}>
              <RefreshRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {(loading || busy) && <LinearProgress />}

      {error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}
            action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Provo përsëri</Button>}>
            {error}
          </Alert>
        </Box>
      )}

      {/* Boshllëk ose mbivendosje mes etapave raportohet, nuk bllokohet: pas një
          zhvendosjeje me dorë mund të jetë krejt i qëllimshëm — por duhet të
          duket tani, jo të zbulohet kur paga nuk përputhet. */}
      {!error && data?.Continuity && !data.Continuity.ok && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 700, fontSize: '0.82rem' }}>Etapat nuk lidhen zinxhir</AlertTitle>
            <Typography sx={{ fontSize: '0.8rem' }}>
              {data.Continuity.issues.map((i) => (
                i.kind === 'gap'
                  ? `Ka boshllëk mes etapës ${i.between[0]} dhe ${i.between[1]}.`
                  : `Etapa ${i.between[0]} dhe ${i.between[1]} mbivendosen.`
              )).join(' ')}
            </Typography>
          </Alert>
        </Box>
      )}

      {loading ? (
        <Stack spacing={1} sx={{ p: 2 }}>{[...Array(3)].map((_, i) => <Skeleton key={i} height={54} />)}</Stack>
      ) : !error && stages.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: canManage ? 1.5 : 0 }}>
            Etapat nuk janë gjeneruar ende për këtë kërkesë.
          </Typography>
          {canManage && (
            <Stack spacing={1.25} alignItems="center">
              <Box sx={{ textAlign: 'left' }}>{unpaidToggle()}</Box>
              <Button variant="contained" size="small" onClick={() => generate()} disabled={busy}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                Gjenero etapat
              </Button>
            </Stack>
          )}
        </Box>
      ) : !error && (
        <Box>
          {/* Zgjedhja mbetet e arritshme edhe pasi etapat janë krijuar: fazë
              opsionale do të thotë që mund edhe të hiqet, jo vetëm të mos shtohet.
              Prekja e saj i rishkruan etapat nga data e fillimit. */}
          {canManage && (
            <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              {unpaidToggle((next) => generate(next))}
            </Box>
          )}
          <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Etapat
          </Typography>
          {stages.map((st, i) => (
            <React.Fragment key={st.Id ?? st.StageNo}>
              {i > 0 && <Divider />}
              <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{
                  width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                  bgcolor: 'action.hover', color: 'text.secondary',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.72rem', fontWeight: 800,
                }}>
                  {st.StageNo}
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.83rem', fontWeight: 700, lineHeight: 1.3 }}>
                    {st.PayerLabel || PAYER_LABEL[st.Payer] || st.Payer}
                  </Typography>
                  <Typography className="tabular" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                    {fmtRange(st.StartDate, st.EndDate)}
                    {st.Months != null && ` · ${st.Months} muaj`}
                    {st.RatePercent != null && ` · ${st.RatePercent}%`}
                  </Typography>
                  {st.Note && (
                    <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.25 }}>{st.Note}</Typography>
                  )}
                </Box>
                <StatusChip status={st.Status} size="sm" />
                {/* Mbivendosja e statusit duhet të duket si mbivendosje, jo si
                    fakt: ndryshe askush s'e di pse datat thonë një gjë dhe
                    pilula një tjetër. */}
                {st.StatusOverride && st.StatusOverride !== st.DerivedStatus && (
                  <Tooltip title={`Sipas datave: ${st.DerivedStatus}`}>
                    <Typography sx={{ fontSize: '0.68rem', color: 'warning.main', fontWeight: 700 }}>manual</Typography>
                  </Tooltip>
                )}
                {canManage && st.Id && (
                  <Tooltip title="Ndrysho datat e etapës">
                    <IconButton size="small" onClick={() => setEditing(st)} sx={{ width: 28, height: 28 }}>
                      <EditRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      )}

      {canManage && (
        <StageDialog
          stage={editing}
          onClose={() => setEditing(null)}
          onSaved={(next) => { setEditing(null); setData(next); }}
          onError={setError}
        />
      )}
    </Paper>
  );
}

/* ─── Zhvendosja e një etape ──────────────────────────────────────────────── */
/* Paguesi dhe numri i etapës nuk janë këtu me qëllim: ato e përcaktojnë se
 * CILA etapë është kjo. Ndryshimi i tyre do të thoshte etapë tjetër, jo e
 * njëjta e korrigjuar — dhe serveri i refuzon gjithsesi. */
function StageDialog({ stage, onClose, onSaved, onError }) {
  const [from, setFrom]   = useState('');
  const [to, setTo]       = useState('');
  const [note, setNote]   = useState('');
  const [saving, setSave] = useState(false);
  const [err, setErr]     = useState('');

  useEffect(() => {
    setFrom(stage?.StartDate || '');
    setTo(stage?.EndDate || '');
    setNote(stage?.Note || '');
    setErr('');
  }, [stage]);

  if (!stage) return null;

  const invalid = !from || !to || to < from;

  const submit = async () => {
    setSave(true); setErr('');
    try {
      const res = await authFetch(`/api/maternity-leave/stages/${stage.Id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ StartDate: from, EndDate: to, Note: note }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.status === 403) { setErr('Nuk keni leje për ta ndryshuar këtë etapë.'); return; }
      if (!res.ok || json.success === false) { setErr(json.error || `Gabim (HTTP ${res.status}).`); return; }
      onSaved(json.data || null);
    } catch {
      setErr('Nuk u lidh dot me serverin.');
      onError?.('Nuk u lidh dot me serverin.');
    } finally { setSave(false); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>
        Etapa {stage.StageNo} — {stage.PayerLabel || PAYER_LABEL[stage.Payer] || stage.Payer}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.75} sx={{ pt: 0.5 }}>
          <Alert severity="info" sx={{ fontSize: '0.78rem', borderRadius: 1.5 }}>
            Kërkesa e pushimit mbetet një e vetme. Këtu zhvendoset vetëm kjo etapë —
            paguesi dhe radha e saj nuk ndryshojnë.
          </Alert>
          <Stack direction="row" spacing={1.5}>
            <TextField size="small" fullWidth type="date" label="Nga *" value={from}
              onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" fullWidth type="date" label="Deri *" value={to}
              onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
          <TextField size="small" fullWidth multiline minRows={2} label="Shënim"
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="P.sh. lindje e parakohshme — datat u zhvendosën" />
          {invalid && (
            <Typography variant="caption" color="error">
              Data e mbarimit nuk mund të jetë para fillimit.
            </Typography>
          )}
          {err && <Alert severity="error" sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" onClick={submit} disabled={saving || invalid}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? 'Duke ruajtur…' : 'Ruaj etapën'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
